import {
  Center,
  Stack,
  Title,
  Text,
  Button,
  Paper,
  ThemeIcon,
  Group,
} from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useLogout } from "@livingsyncs/ui";

/**
 * Auditor's own copy of frontend's `NoAccessPage.tsx` (frontend split epic
 * Phase 6) — shown when RouteGuard refuses a route. Identical content to
 * admin's own copy; only the useAuth/useLogout import is repointed to
 * @livingsyncs/ui.
 */
interface NoAccessState {
  requiredPermissions?: string[];
  from?: string;
}

export function NoAccessPage() {
  const { data: context } = useAuth();
  const { state } = useLocation() as { state: NoAccessState | null };
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hasNoRole = (context?.roles?.length ?? 0) === 0;
  const required = state?.requiredPermissions ?? [];

  const handleCheckAgain = async () => {
    await queryClient.invalidateQueries({ queryKey: ["auth-context"] });
    navigate("/");
  };

  return (
    <Center h="100vh" bg="var(--mantine-color-gray-light)">
      <Paper withBorder shadow="md" p="xl" radius="md" maw={480} w="100%">
        <Stack align="center" gap="lg">
          <ThemeIcon size={64} radius="xl" color="orange" variant="light">
            <IconLock size={32} />
          </ThemeIcon>

          <Stack align="center" gap="xs">
            <Title order={1} fz="h2">
              {hasNoRole ? "Access Pending" : "Permission Required"}
            </Title>
            {hasNoRole ? (
              <Text c="dimmed" ta="center" size="sm">
                Your account has been created but no role has been assigned yet.
                Contact your administrator to gain access to LivingSyncs.
              </Text>
            ) : (
              <Text c="dimmed" ta="center" size="sm">
                Your account does not have permission to open this page. Contact
                your administrator if you need access.
              </Text>
            )}
            {!hasNoRole && required.length > 0 && (
              <Text c="dimmed" ta="center" size="xs">
                {state?.from ? `${state.from} requires ` : "Requires "}
                <strong>{required.join(", ")}</strong>
              </Text>
            )}
          </Stack>

          {context?.email && (
            <Text size="sm" c="dimmed">
              Signed in as <strong>{context.email}</strong>
            </Text>
          )}

          <Group>
            <Button variant="default" onClick={handleCheckAgain}>
              Check Again
            </Button>
            <Button
              color="red"
              variant="light"
              onClick={() => logout()}
              loading={isLoggingOut}
            >
              Sign Out
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Center>
  );
}
